<script setup>
import { computed } from "vue";
import { FIELD_LABELS, isRequired } from "./companySettingsRules";
import { useCompanySettings } from "./useCompanySettings";

const props = defineProps({
  field: { type: String, required: true },
  label: { type: String, default: "" },
  hint: { type: String, default: "" },
  type: { type: String, default: "text" }, // text | email | tel | textarea | select
  options: { type: Array, default: () => [] }, // [{ value, label }] for select
  placeholder: { type: String, default: "" },
  maxlength: { type: [Number, String], default: null },
  rows: { type: Number, default: 3 },
  inputmode: { type: String, default: null },
  uppercase: { type: Boolean, default: false },
});

const { form, canEdit, update, touch, visibleError } = useCompanySettings();

const id = computed(() => `cs-${props.field}`);
const error = computed(() => visibleError(props.field));
const required = computed(() => isRequired(props.field, form));
const labelText = computed(() => props.label || FIELD_LABELS[props.field] || props.field);
const value = computed(() => form[props.field] ?? "");

const onInput = (event) => update(props.field, event.target.value);
</script>

<template>
  <div class="cs-field" :class="{ 'has-error': error }">
    <label :for="id" class="cs-label">
      {{ labelText }}
      <span v-if="required" class="cs-required" aria-hidden="true">*</span>
      <span v-else class="cs-optional">optional</span>
    </label>

    <slot v-bind="{ id, error, value, onInput }">
      <textarea
        v-if="type === 'textarea'"
        :id="id"
        class="form-control"
        :rows="rows"
        :value="value"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :disabled="!canEdit"
        :aria-invalid="!!error"
        @input="onInput"
        @blur="touch(field)"
      ></textarea>
      <select
        v-else-if="type === 'select'"
        :id="id"
        class="form-select"
        :value="value"
        :disabled="!canEdit"
        :aria-invalid="!!error"
        @change="onInput"
        @blur="touch(field)"
      >
        <option value="">{{ placeholder || "Select" }}</option>
        <option v-for="option in options" :key="option.value" :value="option.value">{{ option.label }}</option>
      </select>
      <input
        v-else
        :id="id"
        class="form-control"
        :class="{ 'text-uppercase': uppercase }"
        :type="type"
        :value="value"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :inputmode="inputmode"
        :disabled="!canEdit"
        :aria-invalid="!!error"
        autocomplete="off"
        @input="onInput"
        @blur="touch(field)"
      />
    </slot>

    <div v-if="error" class="cs-error" role="alert">{{ error }}</div>
    <div v-else-if="hint" class="cs-hint">{{ hint }}</div>
    <slot name="after" />
  </div>
</template>
